import React, { useEffect, useRef, useState } from 'react';

export type RecorderState = 'idle' | 'recording' | 'paused';

export interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob | null) => void;
  className?: string;
  autoStart?: boolean;
}

/**
 * Lightweight self-contained audio recorder (MediaRecorder API).
 * Purely additive – emits a Blob via callback, no external side-effects.
 */
export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onRecordingComplete,
  className = '',
  autoStart = false,
}) => {
  const [recState, setRecState] = useState<RecorderState>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const initRecorder = async () => {
    try {
      // Guard: API unavailable in non-secure contexts or unsupported browsers
      if (!('mediaDevices' in navigator) || !navigator.mediaDevices.getUserMedia) {
        console.error('AudioRecorder: navigator.mediaDevices.getUserMedia is not available. This usually means the page is not served over HTTPS (or http://localhost) or the browser does not support the API.');
        alert('Microphone access requires a secure context (https:// or http://localhost). Please reload the application using a secure origin or enable HTTPS.');
        onRecordingComplete(null);
        return null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Build a list of mime candidates ordered by preference.
      const mimeCandidates: string[] = [];
      if (MediaRecorder.isTypeSupported('audio/wav')) mimeCandidates.push('audio/wav');
      // Chrome reliably supports WebM/Opus
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeCandidates.push('audio/webm;codecs=opus');
      mimeCandidates.push('audio/webm');

      let mr: MediaRecorder | null = null;
      for (const mime of mimeCandidates) {
        try {
          mr = new MediaRecorder(stream, { mimeType: mime });
          console.info('[AudioRecorder] Using mimeType', mime);
          break;
        } catch (err) {
          console.warn('[AudioRecorder] mimeType unsupported', mime, err);
        }
      }

      if (!mr) {
        alert('Sorry, your browser does not support audio recording.');
        stream.getTracks().forEach((t) => t.stop());
        return null;
      }

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob =
          chunksRef.current.length > 0
            ? new Blob(chunksRef.current, { type: mr.mimeType })
            : null;
        onRecordingComplete(blob);
        chunksRef.current = [];
        stream.getTracks().forEach((t) => t.stop());
        setRecState('idle');
      };
      mediaRecorderRef.current = mr;
      return mr;
    } catch (err) {
      console.error('AudioRecorder', err);
      onRecordingComplete(null);
      return null;
    }
  };

  const handleStart = async () => {
    if (recState !== 'idle') return;
    const mr = mediaRecorderRef.current ?? (await initRecorder());
    if (!mr) return;
    chunksRef.current = [];
    try {
      mr.start();
      setRecState('recording');
    } catch (err) {
      console.error('[AudioRecorder] Failed to start recorder', err);
      // Fallback: try re-initialising without WAV
      if (mr.mimeType === 'audio/wav') {
        mediaRecorderRef.current = null;
        const fallbackRecorder = await initRecorder();
        if (fallbackRecorder) {
          try {
            fallbackRecorder.start();
            setRecState('recording');
          } catch (err2) {
            console.error('[AudioRecorder] Fallback recorder also failed', err2);
            alert('Unable to start audio recording.');
          }
        }
      }
      return;
    }
  };

  const handlePause = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') {
      mr.pause();
      setRecState('paused');
    }
  };

  const handleResume = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'paused') {
      mr.resume();
      setRecState('recording');
    }
  };

  const handleStop = () => {
    const mr = mediaRecorderRef.current;
    if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
      mr.stop();
    }
  };

  useEffect(() => {
    if (autoStart) handleStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`flex flex-col items-center ${className}`.trim()}>
      <p className="mb-4 text-sm text-slate-600">
        {recState === 'idle' && 'Ready'}
        {recState === 'recording' && 'Recording…'}
        {recState === 'paused' && 'Paused'}
      </p>
      <div className="flex gap-2">
        {recState === 'idle' && (
          <button
            type="button"
            onClick={handleStart}
            className="px-4 py-2 bg-indigo-600 text-white rounded"
          >
            Start
          </button>
        )}
        {recState === 'recording' && (
          <>
            <button
              type="button"
              onClick={handlePause}
              className="px-3 py-2 bg-yellow-500 text-white rounded"
            >
              Pause
            </button>
            <button
              type="button"
              onClick={handleStop}
              className="px-3 py-2 bg-red-600 text-white rounded"
            >
              Stop
            </button>
          </>
        )}
        {recState === 'paused' && (
          <>
            <button
              type="button"
              onClick={handleResume}
              className="px-3 py-2 bg-green-600 text-white rounded"
            >
              Resume
            </button>
            <button
              type="button"
              onClick={handleStop}
              className="px-3 py-2 bg-red-600 text-white rounded"
            >
              Stop
            </button>
          </>
        )}
      </div>
    </div>
  );
};
